import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Card, InputGroup } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { 
    createCollectible, updateCollectible, getCollectible, getFornitori,
    getSuggerimentiCategorieProdotto, getSuggerimentiBrands, getSuggerimentiLineeProdotto, 
    getSuggerimentiLicenze, getSuggerimentiPersonaggi, getSuggerimentiArtisti, 
    getSuggerimentiMateriali 
} from '../services/api';
import { calcolaPrezzoIbs, useImpostazioniPrezzi } from '../utils/prezziIbs';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTornaIndietro } from '../common/navigazione';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave } from '@fortawesome/free-solid-svg-icons';

const CollectibleEdit = () => {
  const initialFormData = {
    nome: '',
    descrizione: '',
    sku: '',
    barcode: '',
    prezzo_acquisto: '',
    prezzo_vendita: '',
    prezzo_vendita_base: '',
    prezzo_ibs: '',
    aliquota_iva: 22,
    quantita: 0,
    fornitore_id: '',
    immagine_url: '',
    pubblica_su_web: true,
    da_pubblicare_su_ibs: false,
    categoria_prodotto: '',
    brand: '',
    linea_prodotto: '',
    licenza: '',
    personaggio: '',
    artista: '',
    materiale: '',
    altezza_cm: '',
    larghezza_cm: '',
    lunghezza_cm: '',
    data_uscita: '',
    attributi_specifici: 'null', // Come stringa JSON
  };

  const [formData, setFormData] = useState(initialFormData);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const backTo = location.state?.from || '/collectibles';
  const tornaIndietro = useTornaIndietro('/collectibles');

  // Stati per i suggerimenti delle Typeahead
  const [categorieProdottoSuggestions, setCategorieProdottoSuggestions] = useState([]);
  const [brandsSuggestions, setBrandsSuggestions] = useState([]);
  const [lineeProdottoSuggestions, setLineeProdottoSuggestions] = useState([]);
  const [licenzeSuggestions, setLicenzeSuggestions] = useState([]);
  const [personaggiSuggestions, setPersonaggiSuggestions] = useState([]);
  const [artistiSuggestions, setArtistiSuggestions] = useState([]);
  const [materialiSuggestions, setMaterialiSuggestions] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const { percentualeIbs, markupFissoIbs } = useImpostazioniPrezzi();

  // Doppio clic dalla lista su una colonna di prezzo/quantità: porta direttamente a quella sezione
  useEffect(() => {
    if (!location.hash || (id && !formData.nome)) return;
    const target = document.getElementById(location.hash.slice(1));
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = target.querySelector('input, select, textarea');
      if (input) input.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, formData.nome]);

  // Funzione unificata per gestire la selezione e l'inserimento in Typeahead
  const handleTypeaheadChange = (name, selected) => {
    if (selected.length === 0) {
      return;
    }
    const selection = selected[0];
    const value = selection.custom ? selection.label : (typeof selection === 'object' ? selection.label : selection);
    setFormData({ ...formData, [name]: value });
  };

  const handleTypeaheadInputChange = (name, text) => {
    setFormData({ ...formData, [name]: text });
  };

  useEffect(() => {
    // Gestione anteprima immagine
    if (selectedFile) {
      const objectUrl = URL.createObjectURL(selectedFile);
      setPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (formData.immagine_url) {
      const serverBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace('/api', '');
      const imageUrl = `${serverBaseUrl}${formData.immagine_url.startsWith('/') ? '' : '/'}${formData.immagine_url}`;
      setPreview(imageUrl);
      return;
    }
    setPreview(undefined);
  }, [selectedFile, formData.immagine_url]);

  useEffect(() => {
    // Caricamento suggerimenti per tutte le Typeahead
    const fetchSuggestions = async () => {
      try {
        const [
          categorieProdottoRes, brandsRes, lineeProdottoRes, licenzeRes, 
          personaggiRes, artistiRes, materialiRes, fornitoriRes
        ] = await Promise.all([
          getSuggerimentiCategorieProdotto(),
          getSuggerimentiBrands(),
          getSuggerimentiLineeProdotto(),
          getSuggerimentiLicenze(),
          getSuggerimentiPersonaggi(),
          getSuggerimentiArtisti(),
          getSuggerimentiMateriali(),
          getFornitori(),
        ]);
        setCategorieProdottoSuggestions(categorieProdottoRes.data);
        setBrandsSuggestions(brandsRes.data);
        setLineeProdottoSuggestions(lineeProdottoRes.data);
        setLicenzeSuggestions(licenzeRes.data);
        setPersonaggiSuggestions(personaggiRes.data);
        setArtistiSuggestions(artistiRes.data);
        setMaterialiSuggestions(materialiRes.data);
        setFornitori(fornitoriRes.data.fornitori);
      } catch (error) {
        console.error("Errore nel caricamento dei suggerimenti:", error);
      }
    };
    fetchSuggestions();

    // Caricamento dati collezionabile
    if (id) {
      const fetchCollectible = async () => {
        try {
          const response = await getCollectible(id);
          const collectibleData = response.data;
          setFormData({
            ...collectibleData,
            prezzo_acquisto: collectibleData.prezzo_acquisto !== null ? collectibleData.prezzo_acquisto.toString() : '',
            prezzo_vendita: collectibleData.prezzo_vendita !== null ? collectibleData.prezzo_vendita.toString() : '',
            prezzo_vendita_base: collectibleData.prezzo_vendita_base != null ? collectibleData.prezzo_vendita_base.toString() : '',
            prezzo_ibs: collectibleData.prezzo_ibs != null ? collectibleData.prezzo_ibs.toString() : '',
            aliquota_iva: collectibleData.aliquota_iva !== null ? collectibleData.aliquota_iva.toString() : '22',
            quantita: collectibleData.quantita !== null ? collectibleData.quantita.toString() : '0',
            data_uscita: collectibleData.data_uscita ? new Date(collectibleData.data_uscita).toISOString().split('T')[0] : '',
            attributi_specifici: collectibleData.attributi_specifici ? JSON.stringify(collectibleData.attributi_specifici, null, 2) : '', // 'null' diventa ''
            descrizione: collectibleData.descrizione || '', // Assicura che sia una stringa vuota se null
          });
        } catch (error) {
          console.error("Errore nel caricamento del collezionabile:", error);
          alert("Impossibile caricare i dati del collezionabile.");
          tornaIndietro();
        }
      };
      fetchCollectible();
    }
  }, [id, navigate]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'file') return;

    let nextValue = value;
    if (type === 'checkbox') {
      nextValue = checked;
    } else if (type === 'number') {
      nextValue = value === '' ? '' : parseFloat(value);
    }

    const aggiornamenti = { [name]: nextValue };
    if (name === 'prezzo_vendita') {
      // Il prezzo IBS segue sempre il prezzo di vendita; il base segue finché non si usa il markup (🚚)
      aggiornamenti.prezzo_ibs = calcolaPrezzoIbs(nextValue, percentualeIbs, markupFissoIbs) ?? '';
      aggiornamenti.prezzo_vendita_base = nextValue;
    }
    setFormData({
      ...formData,
      ...aggiornamenti
    });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nome || formData.prezzo_vendita === '' || formData.prezzo_vendita === null || formData.prezzo_vendita === undefined || Number.isNaN(formData.prezzo_vendita) || !formData.categoria_prodotto) {
      alert('Compila Nome, Prezzo Vendita e Categoria Prodotto.');
      return;
    }

    const dataToSubmit = new FormData();

    const normalizeValue = (value) => {
      if (value === '' || value === null || value === undefined) return undefined;
      if (typeof value === 'number' && Number.isNaN(value)) return undefined;
      return value;
    };

    Object.keys(formData).forEach(key => {
      const normalizedValue = normalizeValue(formData[key]);
      if (normalizedValue === undefined) return;

      if (typeof normalizedValue === 'boolean') {
        dataToSubmit.append(key, normalizedValue ? 'true' : 'false');
      } else if (key === 'attributi_specifici') {
        dataToSubmit.append('attributi_specifici_json', normalizedValue);
      } else {
        dataToSubmit.append(key, normalizedValue);
      }
    });

    if (selectedFile) {
      dataToSubmit.append('immagine_file', selectedFile);
    }

    try {
      if (id) {
        await updateCollectible(id, dataToSubmit);
        alert('Collezionabile aggiornato con successo!');
      } else {
        await createCollectible(dataToSubmit);
        alert('Collezionabile creato con successo!');
      }
      tornaIndietro();
    } catch (error) {
      console.error(`Errore ${id ? 'nell\'aggiornamento' : 'nella creazione'} del collezionabile:`, error.response || error);
      alert(`Errore ${id ? 'nell\'aggiornamento' : 'nella creazione'} del collezionabile.`);
    }
  };

  return (
    <Container>
      <Row className="align-items-center mb-3">
        <Col>
          <h2>{id ? 'Modifica Collezionabile' : 'Nuovo Collezionabile'}</h2>
        </Col>
        <Col xs="auto" className="ms-auto">
          <Button variant="secondary" onClick={() => tornaIndietro()} className="me-2">
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Torna alla Lista
          </Button>
          <Button variant="primary" type="submit" form="collectible-form">
            <FontAwesomeIcon icon={faSave} className="me-2" />
            {id ? 'Salva Modifiche' : 'Salva Nuovo Collezionabile'}
          </Button>
        </Col>
      </Row>
      <Form id="collectible-form" onSubmit={handleSubmit}>
        {/* Card: CODICI */}
        <Card className="mb-3">
          <Card.Header>CODICI E NOME</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formNome">
                  <Form.Label>Nome</Form.Label>
                  <Form.Control type="text" name="nome" value={formData.nome} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3" controlId="formSku">
                  <Form.Label>SKU</Form.Label>
                  <Form.Control type="text" name="sku" value={formData.sku} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3" controlId="formBarcode">
                  <Form.Label>Barcode</Form.Label>
                  <Form.Control type="text" name="barcode" value={formData.barcode} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Card: Informazioni Principali (verrà dopo) */}

        {/* Card: Categorizzazione */}
        <Card className="mb-3">
          <Card.Header>Categorizzazione</Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Categoria Prodotto</Form.Label>
                  <Typeahead
                    id="categoria_prodotto-typeahead"
                    options={categorieProdottoSuggestions}
                    allowNew
                    onChange={(selected) => handleTypeaheadChange('categoria_prodotto', selected)}
                    onInputChange={(text) => handleTypeaheadInputChange('categoria_prodotto', text)}
                    selected={formData.categoria_prodotto ? [formData.categoria_prodotto] : []}
                    placeholder="Es. Statua, Action Figure..."
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Brand</Form.Label>
                  <Typeahead
                    id="brand-typeahead"
                    options={brandsSuggestions}
                    allowNew
                    onChange={(selected) => handleTypeaheadChange('brand', selected)}
                    onInputChange={(text) => handleTypeaheadInputChange('brand', text)}
                    selected={formData.brand ? [formData.brand] : []}
                    placeholder="Es. Enesco, Funko..."
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Linea Prodotto</Form.Label>
                  <Typeahead
                    id="linea_prodotto-typeahead"
                    options={lineeProdottoSuggestions}
                    allowNew
                    onChange={(selected) => handleTypeaheadChange('linea_prodotto', selected)}
                    onInputChange={(text) => handleTypeaheadInputChange('linea_prodotto', text)}
                    selected={formData.linea_prodotto ? [formData.linea_prodotto] : []}
                    placeholder="Es. Disney Traditions, Q Posket..."
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Licenza</Form.Label>
                  <Typeahead
                    id="licenza-typeahead"
                    options={licenzeSuggestions}
                    allowNew
                    onChange={(selected) => handleTypeaheadChange('licenza', selected)}
                    onInputChange={(text) => handleTypeaheadInputChange('licenza', text)}
                    selected={formData.licenza ? [formData.licenza] : []}
                    placeholder="Es. Disney, Marvel..."
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Personaggio</Form.Label>
                  <Typeahead
                    id="personaggio-typeahead"
                    options={personaggiSuggestions}
                    allowNew
                    onChange={(selected) => handleTypeaheadChange('personaggio', selected)}
                    onInputChange={(text) => handleTypeaheadInputChange('personaggio', text)}
                    selected={formData.personaggio ? [formData.personaggio] : []}
                    placeholder="Es. Cenerentola, Grinch..."
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Artista</Form.Label>
                  <Typeahead
                    id="artista-typeahead"
                    options={artistiSuggestions}
                    allowNew
                    onChange={(selected) => handleTypeaheadChange('artista', selected)}
                    onInputChange={(text) => handleTypeaheadInputChange('artista', text)}
                    selected={formData.artista ? [formData.artista] : []}
                    placeholder="Es. Jim Shore..."
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Card: Dettagli Prodotto */}
        <Card className="mb-3">
          <Card.Header>Dettagli Prodotto</Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Descrizione</Form.Label>
              <Form.Control as="textarea" rows={3} name="descrizione" value={formData.descrizione} onChange={handleChange} />
            </Form.Group>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Materiale</Form.Label>
                  <Typeahead
                    id="materiale-typeahead"
                    options={materialiSuggestions}
                    allowNew
                    onChange={(selected) => handleTypeaheadChange('materiale', selected)}
                    onInputChange={(text) => handleTypeaheadInputChange('materiale', text)}
                    selected={formData.materiale ? [formData.materiale] : []}
                    placeholder="Es. Resina, PVC..."
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Data Uscita</Form.Label>
                  <Form.Control type="date" name="data_uscita" value={formData.data_uscita} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Altezza (cm)</Form.Label>
                  <Form.Control type="number" step="0.01" name="altezza_cm" value={formData.altezza_cm} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Larghezza (cm)</Form.Label>
                  <Form.Control type="number" step="0.01" name="larghezza_cm" value={formData.larghezza_cm} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Lunghezza (cm)</Form.Label>
                  <Form.Control type="number" step="0.01" name="lunghezza_cm" value={formData.lunghezza_cm} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>


        {/* Card: Immagine */}
        <Card className="mb-3">
          <Card.Header>Immagine</Card.Header>
          <Card.Body>
            <Row>
              <Col md={8}>
                <Form.Group controlId="formImmagineFile" className="mb-3">
                    <Form.Label>Carica Immagine</Form.Label>
                    <Form.Control type="file" onChange={handleFileChange} />
                </Form.Group>
                <input type="hidden" name="immagine_url" value={formData.immagine_url || ''} />
              </Col>
              <Col md={4}>
                <Form.Label>Anteprima</Form.Label>
                <div className="text-center border p-2" style={{ minHeight: '150px' }}>
                  {preview ? (
                    <img src={preview} alt="Anteprima" className="img-fluid" />
                  ) : (
                    <span>Nessuna immagine</span>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Card: Prezzi e Quantità */}
        <Card className="mb-3" id="prezzi">
          <Card.Header>Prezzi e Quantità</Card.Header>
          <Card.Body>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Prezzo Vendita</Form.Label>
                  <InputGroup>
                    <Form.Control type="number" step="0.01" name="prezzo_vendita" value={formData.prezzo_vendita} onChange={handleChange} required />
                  </InputGroup>
                  {formData.prezzo_vendita_base !== '' && formData.prezzo_vendita_base != null && (
                    <Form.Text className="text-muted">Prezzo base: €{Number(formData.prezzo_vendita_base).toFixed(2)}</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Prezzo IBS <span className="text-muted small">(dal prezzo base: +{percentualeIbs}% +{markupFissoIbs}€, solo informativo)</span></Form.Label>
                  <Form.Control type="number" step="0.01" name="prezzo_ibs" value={formData.prezzo_ibs || ''} disabled readOnly />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Prezzo Acquisto</Form.Label>
                  <Form.Control type="number" step="0.01" name="prezzo_acquisto" value={formData.prezzo_acquisto} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Aliquota IVA %</Form.Label>
                  <Form.Control type="number" step="1" name="aliquota_iva" value={formData.aliquota_iva} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Quantità</Form.Label>
                  <Form.Control type="number" step="1" name="quantita" value={formData.quantita} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        
        {/* Card: Fornitore e Pubblicazione */}
        <Card className="mb-3">
          <Card.Header>Fornitore e Pubblicazione</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                 <Form.Group className="mb-3">
                    <Form.Label>Fornitore</Form.Label>
                    <Typeahead
                        id="fornitore-typeahead"
                        labelKey="denominazione"
                        options={fornitori}
                        selected={fornitori.filter(f => f.id === formData.fornitore_id)}
                        onChange={(selected) => {
                            const fornitoreId = selected.length > 0 ? selected[0].id : null;
                            setFormData({ ...formData, fornitore_id: fornitoreId });
                        }}
                        placeholder="Seleziona un fornitore..."
                    />
                </Form.Group>
              </Col>
              <Col md={6} className="d-flex align-items-center">
                 <Form.Group className="mb-3 d-flex align-items-center"> {/* Use d-flex to align multiple checks if needed */}
                  <Form.Check
                    type="checkbox"
                    label="Pubblica su Web"
                    name="pubblica_su_web"
                    checked={formData.pubblica_su_web}
                    onChange={handleChange}
                    className="me-3" // Add margin for spacing
                  />
                  <Form.Check
                    type="checkbox"
                    label="In Vetrina"
                    name="in_vetrina"
                    checked={formData.in_vetrina || false}
                    onChange={handleChange}
                    className="me-3"
                  />
                  <Form.Check
                    type="checkbox"
                    label="Presente su IBS"
                    name="da_pubblicare_su_ibs"
                    checked={formData.da_pubblicare_su_ibs || false}
                    onChange={handleChange}
                    title="Offerta già creata a mano su IBS: quantità e prezzo IBS vengono allineati automaticamente (l'offerta non viene creata dal sistema)"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Card: Attributi Avanzati */}
        <Card className="mb-4">
            <Card.Header>Attributi Avanzati</Card.Header>
            <Card.Body>
                 <Form.Group className="mb-3">
                  <Form.Label>Attributi Specifici (JSON)</Form.Label>
                  <Form.Control as="textarea" rows={5} name="attributi_specifici" value={formData.attributi_specifici} onChange={handleChange} />
                </Form.Group>
            </Card.Body>
        </Card>

      </Form>
    </Container>
  );
};

export default CollectibleEdit;
